import * as Sentry from "@hy-sentry/browser"
import { Integrations } from "@hy-sentry/tracing"
import { parseSearch, parseSdkUrl, getCookie } from './utils'
import queryString from 'query-string'
import proxyConsole from './proxyConsole'

// 暴露给业务
window.Sentry = Sentry

declare global {
  interface Window {
    hyExt: any
    Sentry: any
    Integrations: any
    __os: any
    performanceInfo: any
  }
}
export type Env = 'unuse' | 'dev' | 'test' | 'prod' | 'unknown'

const SDK_CDN_ERROR_URL = '//extsdk-msstatic.cdn.huya.com/api/extsdkdeliver/reportDeliverError'
const META_LIST = [
  'ext-design-width',
  'ext-type',
  'ext-platform',
  'ext-uuid',
  'ext-name',
  'ext-version',
  'ext-version-id',
  'ext-tech-type',
  'ext-busi-type',
  'ext-build-env',
]

const COOKIE_TAG_KEYS = [
  'udb_uid'
]

const DSN = {
  production: 'https://c7e00bfbba9a4a52b90d641213c4abb4@bee-wpm.huya.com/171',
  test: 'https://f0071fb18ff241eea6eb3d85ea6caa96@bee-wpm.huya.com/172',
  dev: 'https://f0071fb18ff241eea6eb3d85ea6caa96@bee-wpm.huya.com/172',
}

function getCustomSentryTags(win: Window, sentryDebug: boolean) {
  const reducer = (acc: object, [tagKey, tagValue]: [string, unknown]) => ({
    ...acc,
    [`external_${tagKey}`]: tagValue
  })

  let tags = {}

  try {
    if (win.location.hash.includes('sentryTags')) {
      if (sentryDebug) { console.log(`sentry Tags in location: ${win.location.hash}`) }
      const sentryTags = JSON.parse(
        queryString.parse(win.location.hash.slice(1)).sentryTags as string
      )
      tags = Object.entries(sentryTags).reduce(reducer, tags)
    }

    if (typeof win.hyExt.sentryTags !== 'undefined') {
      if (sentryDebug) { console.log(`sentry Tags in hyExt: ${JSON.stringify(win.hyExt.sentryTags)}`) }
      tags = Object.entries(win.hyExt.sentryTags).reduce(reducer, tags)
    }

    return tags
  } catch (err) {
    if (sentryDebug) { console.log(`getCustomSentryTags error: ${err?.message}`) }
    return tags

  }
}

function getUserInfo(win: Window) {
  // @ts-ignore
  if (window?.external?.ExtExtra) {
    // @ts-ignore
    return window.external.ExtExtra
  }

  const raw = parseSearch(win?.location?.search || '')?.extextra
  if (raw) {
    const [suid, spid, room, gid] = decodeURIComponent(raw).split('|')
    return {
      suid,
      spid,
      room,
      gid,
    }
  }

  return {}
}

function getMetaContentList(doc: Document, metas: string[]) {
  const getMetaContent = (doc: Document, name: string) => {
    try {
      return doc
        .querySelector(`meta[name='${name}']`)
        .getAttribute('content')
    } catch (err) {
      return ''
    }
  }
  return metas.reduce((acc, cur) => {
    return {
      ...acc,
      [cur.replace(/-/g, '_')]: getMetaContent(doc, cur),
    }
  }, {})
}

function getCookieTags(doc: Document) {
  return COOKIE_TAG_KEYS.reduce((pre, cur) => {
    const cookieValue = getCookie(doc, cur);
    if (!!cookieValue) {
      return {
        ...pre,
        [cur]: cookieValue
      }
    }
    return pre
  }, {})
}

function setupSentry(win: Window, doc: Document, env: Env, metaContents: { [key: string]: string }) {
  const {
    ext_uuid,
    ext_version,
    ext_version_id,
    ext_build_env,
  } = metaContents

  if (!ext_uuid || !ext_version || !ext_version_id) {
    // 没有这几个数据, 说明可能 1. 是开发版本的 2. 非正常渠道构建的(比如hyui doc)
    // 不需要接入 sentry
    return
  }

  const params = parseSearch(win?.location?.search || '')
  const sentryDebug = params.sentryDebug === '1'
  const dsn = params.sentryDsn ? decodeURIComponent(params.sentryDsn) : DSN[ext_build_env]

  if (!dsn) {
    return
  }

  const initParams = {
    dsn,
    /**
     * tracing 配置，对应 fems performance 模块
     * integrations: 初始化 tracing 插件
     * tracesSampleRate: transaction 采集比率，注意设置为 0.01
     */
    integrations: [new Integrations.BrowserTracing({
      notTracingRemote: true
    })],
    tracesSampleRate: params.tracesSampleRate === undefined ? 0.001 : parseFloat(params.tracesSampleRate),
    release: `${ext_uuid}@${ext_version}`,
    initialScope: {
      tags: {
        ...Object.keys(metaContents).reduce((acc, cur) => {
          return {
            ...acc,
            [cur.replace(/_/g, '-')]: metaContents[cur],
          }
        }, {}),
        ...getUserInfo(win),
        ...getCustomSentryTags(win, sentryDebug),
        ...getCookieTags(doc),
        h5SessionId: win?.performanceInfo?.pageGid || "noH5SessionId",
      }
    },
    debug: sentryDebug,
    environment: env,
    sampleRate: params.sampleRate === undefined ? 1 : parseFloat(params.sampleRate),
    ignoreErrors: [
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      /^(ResizeObserver loop ).*/g
    ]
  }

  if (sentryDebug) {
    console.log('sentry initParams: ', initParams)
  }
  Sentry.init(initParams)

  // 开启hook logger
  if (params.sentryHookLogger) {
    proxyConsole(Sentry, win?.hyExt?.logger, sentryDebug)
  }
}


function attachMetaToWindow(win: Window, metas: { [key: string]: string }) {
  Object.keys(metas).forEach(key => {
    const property = `__HY${key.toUpperCase()}`
    win[property] = metas[key]
  })

  if (!win.__os && metas.ext_busi_type !== 'ext') {
    window.__os = metas.ext_platform
  }
}

function reportSDKCDNError<T extends {
  msg: string
}>(payload: T) {
  const xhr = new XMLHttpRequest()
  xhr.open('POST', SDK_CDN_ERROR_URL, true)


  const uniformPayload = (data) => {
    return Object.keys(data).reduce((acc, cur) => {
      const key = cur.split('_').map((xs, idx) => idx === 0 ? xs : `${xs[0].toUpperCase()}${xs.slice(1)}`).join('')
      return {
        ...acc,
        [key]: data[cur],
      }
    }, {})
  }

  const data = JSON.stringify(uniformPayload(payload))

  xhr.setRequestHeader('Content-Type', 'application/json')
  xhr.send(data)
}

async function main(win: Window, doc: Document) {
  const metaContent: any = getMetaContentList(doc, META_LIST)

  attachMetaToWindow(win, metaContent)

  if (!metaContent?.ext_uuid) {
    return
  }

  let environment = 'unknown'
  try {
    const extInfo = await win.hyExt.env.getExtInfo()
    environment = ['unuse', 'dev', 'test', 'prod'][parseInt(extInfo.extVersionType, 10)]
  } catch (err) {
    const extSdkUrl = parseSearch(win?.location?.search || '').rnbaseurl
    const extSdkVersion = parseSdkUrl(extSdkUrl)

    reportSDKCDNError({
      ...metaContent,
      extSdkUrl,
      extSdkVersion,
      errorStack: err?.stack,
      msg: err?.message,
    })
  } finally {
    setupSentry(win, doc, environment as Env, metaContent)
  }
}

main(window, document)
